import { useEffect, useMemo } from "react";
import { chunk, groupBy, isEqual } from "lodash";
import { usePrevious } from "react-use";
import { useSecurityImagesContext, Image } from "@providers";
import { getBucketBaseUrl, fetchFromBucket } from "./bucket";
import { fetchFromApi } from "./api";
import {
  Security,
  getIdsToFetchForProvider,
  isCryptoProvider,
  markFetching,
  markResolved,
} from "./helpers";

const CHUNK_SIZE = 10;

export const useSecurityImages = <T extends Security>(
  data?: T[]
): Record<string, string | null | undefined> => {
  const groupByQuote = useMemo(
    () =>
      groupBy(data, (item) => item.quote?.quoteProvider || item.quoteProvider),
    [data]
  );
  const prevGroupByQuote = usePrevious(groupByQuote);
  const { images, setImages } = useSecurityImagesContext();

  useEffect(() => {
    if (isEqual(groupByQuote, prevGroupByQuote)) return;

    const processChunk = async (quoteProvider: string, ids: string[]) => {
      setImages((prev) => ({ ...prev, ...markFetching(prev, ids) }));

      let toApi = ids;
      if (getBucketBaseUrl()) {
        const size = isCryptoProvider(quoteProvider) ? "large" : "medium";
        const { found, missing } = await fetchFromBucket(ids, size);
        if (found.length) {
          setImages((prev) => ({ ...prev, ...markResolved(found) }));
        }
        toApi = missing;
      }

      if (toApi.length) {
        const patch = await fetchFromApi(quoteProvider, toApi);
        setImages((prev) => ({ ...prev, ...patch }));
      }
    };

    const processProvider = async (quoteProvider: string) => {
      if (quoteProvider === "none") return;

      const ids = getIdsToFetchForProvider(quoteProvider, groupByQuote, images);
      if (!ids.length) return;

      await Promise.all(
        chunk(ids, CHUNK_SIZE).map((idsChunk) =>
          processChunk(quoteProvider, idsChunk)
        )
      );
    };

    void Promise.all(Object.keys(groupByQuote).map(processProvider));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupByQuote, prevGroupByQuote]);

  return useMemo(
    () =>
      Object.entries(images).reduce(
        (acc, [key, value]: [string, Image]) => ({
          ...acc,
          [key]: value?.value,
        }),
        {} as Record<string, string | null | undefined>
      ),
    [images]
  );
};
